import React, { createContext, useContext } from 'react';
import { useAcademicExpiration } from '../hooks/useAcademicExpiration';
import AcademicExpirationModal from '@/components/Academic/AcademicExpirationModal';
import { useAuthStore } from '../store/authStore';

interface AcademicExpirationContextType {
  openModal: () => void;
  shouldShowExpirationTag: boolean;
  tagInfo: any;
  isAcademic: boolean;
}

const AcademicExpirationContext = createContext<AcademicExpirationContextType | undefined>(undefined);

export const useAcademicExpirationContext = () => {
  const context = useContext(AcademicExpirationContext);
  if (context === undefined) {
    throw new Error('useAcademicExpirationContext must be used within an AcademicExpirationProvider');
  }
  return context;
};

interface AcademicExpirationProviderProps {
  children: React.ReactNode;
  user: any;
  isAuthenticated: boolean;
}

export const AcademicExpirationProvider: React.FC<AcademicExpirationProviderProps> = ({
  children,
  user,
  isAuthenticated,
}) => {
  const {
    expirationStatus,
    showWarningModal,
    warningType,
    loading,
    isAcademic,
    shouldShowExpirationTag,
    tagInfo,
    handleRenewal,
    handleCancellation,
    dismissWarning,
    openModal,
    hasAppliedForRenewal,
    hasCancelledAccount,
    wasRenewalDenied,
    clearPendingRenewalConfirmation,
    wasRenewalApproved,
    currentAcademicPlan,
  } = useAcademicExpiration(user, isAuthenticated);

  const handleRefreshUserAttributes = async () => {
    try {
      const { getCurrentUser } = await import('aws-amplify/auth');
      const { refreshSession } = await import('../services/RefreshSession');
      
      const user = await getCurrentUser();
      const session = await refreshSession({ forceRefresh: true });
      const userInfo = session.tokens.idToken.payload;
      
      const userWithAttributes = {
        ...userInfo,
        ...user,
      };

      const authStore = useAuthStore.getState();
      authStore.setUser(userWithAttributes);
    } catch (error) {
      console.error('Failed to refresh user attributes from context:', error);
      throw error;
    }
  };

  const contextValue = {
    openModal,
    shouldShowExpirationTag,
    tagInfo,
    isAcademic,
  };

  return (
    <AcademicExpirationContext.Provider value={contextValue}>
      {children}

      {/* Global Academic Expiration Modal */}
      {isAcademic && (
        <AcademicExpirationModal
          open={showWarningModal}
          onClose={dismissWarning}
          warningType={warningType}
          expirationStatus={expirationStatus}
          onRenewal={handleRenewal}
          onCancellation={handleCancellation}
          loading={loading}
          onClearPendingConfirmation={clearPendingRenewalConfirmation}
          onRefreshUserAttributes={handleRefreshUserAttributes}
          hasAppliedForRenewal={hasAppliedForRenewal}
          hasCancelledAccount={hasCancelledAccount}
          wasRenewalDenied={wasRenewalDenied}
          wasRenewalApproved={wasRenewalApproved}
          currentAcademicPlan={currentAcademicPlan}
        />
      )}
    </AcademicExpirationContext.Provider>
  );
};
